import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { formatDistanceToNow } from 'date-fns'
import { Briefcase, User, Newspaper, ChevronRight, Bell } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ContactUpdatesDashboard } from '@/lib/types'

const typeIcons: Record<string, typeof Briefcase> = {
  job_change: Briefcase,
  profile_update: User,
  company_news: Newspaper,
  social_post: Newspaper,
}

const typeColors: Record<string, string> = {
  job_change: 'text-lilac-foreground dark:text-lilac',
  profile_update: 'text-coral',
  company_news: 'text-chartreuse-foreground dark:text-chartreuse',
  social_post: 'text-primary',
}

interface Props {
  data: ContactUpdatesDashboard
}

export function ContactUpdatesWidget({ data }: Props) {
  const { t } = useTranslation()

  if (data.unreadCount === 0 && data.recent.length === 0) {
    return null
  }

  return (
    <div className="bg-card rounded-2xl border p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="text-primary h-4 w-4" />
          <h2 className="text-sm font-semibold">
            {t('dashboard.contactUpdates', 'Contact Updates')}
          </h2>
          {data.unreadCount > 0 && (
            <span className="bg-primary text-primary-foreground inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold">
              {data.unreadCount}
            </span>
          )}
        </div>
        <Link
          to="/updates"
          className="text-muted-foreground hover:text-foreground flex items-center gap-0.5 text-xs"
        >
          {t('common.viewAll', 'View all')}
          <ChevronRight className="h-3 w-3" />
        </Link>
      </div>

      <div className="space-y-2">
        {data.recent.map((update) => {
          const Icon = typeIcons[update.updateType] || User
          const color = typeColors[update.updateType] || 'text-muted-foreground'

          return (
            <Link
              key={update.id}
              to={`/contacts/${update.contactId}`}
              className="hover:bg-muted/50 flex items-start gap-2.5 rounded-lg p-2 transition-colors"
            >
              <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', color)} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{update.title}</p>
                <p className="text-muted-foreground text-xs">
                  {formatDistanceToNow(new Date(update.detectedAt), { addSuffix: true })}
                </p>
              </div>
            </Link>
          )
        })}
      </div>
    </div>
  )
}
